import { Dispatch, Fragment, useEffect, useRef, useState } from "react";
import {
  Controller,
  useForm,
  useFieldArray,
  FormProvider,
} from "react-hook-form";
import { Row, Spacer } from "../../../components/Layout/Layout";
import Button from "../../../ui-lib/components/Button/Button";
import useTranslations from "../../../core/i18n/useTranslations";
import {
  IADGroup,
  IADGroupRequest,
  IRolesTemplates,
  ITemplatesItemsProps,
} from "../../../core/api/adGroup/types";
import TextInput from "../../../ui-lib/components/Inputs/TextInput";
import { NestedOrganizationProps } from "../../../core/api/organizations/organizations";
import { RoleTemplateRow } from "./RoleTemplateRow";
import { updateADGroup } from "../../../core/api/adGroup/adGroup";
import useUser from "../../../core/user/useUser";
import { notify } from "../../../ui-lib/components/Alerts/Toast";
import { ApiV2Error } from "../../../core/interfaces/Api";
import PrimeModal from "../../../ui-lib/components/PrimeModal/PrimeModal";
import { IItemProp } from "../../../ui-lib/components/Dropdown/DropdownItem";

export const CreateADGroup = ({
  onClose,
  isOpen,
  adGroupData,
  roleTemplatesData,
  organizationsData,
  organizationId,
  initUpdateTable,
  updateTableFunc,
}: {
  onClose: () => void;
  isOpen: boolean;
  adGroupData?: IADGroup;
  roleTemplatesData: ITemplatesItemsProps[];
  organizationsData: NestedOrganizationProps[];
  organizationId: number | string;
  initUpdateTable: number;
  updateTableFunc: Dispatch<number>;
}) => {
  const { authenticatedRequest } = useUser();
  const t = useTranslations();
  // Cache for ARC data to avoid multiple requests
  const arcDataCache = useRef<Map<number, IItemProp>>(new Map());
  // Unique set of loaded ARC template IDs
  const loadedArcTemplates = useRef<Set<number>>(new Set());

  const [isLoading, setIsLoading] = useState(false);
  const [templateErrors, setTemplateErrors] = useState<string[]>([]);

  const getFormModel = (): IADGroupRequest => {
    if (!adGroupData) {
      return {
        adGroupId: "",
        adGroupName: "",
        roleTemplates: [
          {
            templateId: null,
          },
        ],
      };
    }
    return {
      adGroupId: adGroupData.adGroupId,
      adGroupName: adGroupData.adGroupName,
      roleTemplates: [
        ...adGroupData.roleMappings.map((role) => {
          const orgNameToUse =
            role.type === "customer" ? "organizationId" : "responseCenterId";
          const data: IRolesTemplates = {
            templateId: role.roleTemplateId,
          };
          if (orgNameToUse === "organizationId") {
            data.organizationId = role.belongsToId;
          } else {
            data.responseCenterId = role.belongsToId;
          }
          return data;
        }),
      ],
    };
  };

  const formMethods = useForm<IADGroupRequest>({
    defaultValues: getFormModel(),
    mode: "onChange",
  });

  const {
    control,
    handleSubmit,
    getValues,
    reset,
    trigger,
    formState: { errors, isDirty },
  } = formMethods;

  const { fields, append, remove } = useFieldArray({
    control,
    name: "roleTemplates",
  });

  const recomputeTemplateErrors = (roleTemplates: IRolesTemplates[]) => {
    if (!roleTemplates) {
      setTemplateErrors([]);
      return [];
    }

    const counts = new Map<number, number>();
    roleTemplates.forEach((role) => {
      if (role.templateId) {
        counts.set(role.templateId, (counts.get(role.templateId) ?? 0) + 1);
      }
    });

    const allTemplateErrors = roleTemplates.map((role) => {
      if (!role.templateId) {
        // Required error is still handled by react-hook-form rules
        return "";
      }

      const count = counts.get(role.templateId) ?? 0;
      if (count > 1) {
        return t("Errors:NonUniqueData");
      }

      return "";
    });

    setTemplateErrors(allTemplateErrors);
    return allTemplateErrors;
  };

  useEffect(() => {
    if (isOpen) {
      const formModel = getFormModel();
      reset(formModel, { keepDefaultValues: false });
    }
    return () => {
      // Clear caches and template errors on modal close
      arcDataCache.current.clear();
      loadedArcTemplates.current.clear();
      setTemplateErrors([]);
    };
  }, [isOpen, adGroupData]);

  const onSubmit = async (data: IADGroupRequest) => {
    try {
      const fieldErrors = recomputeTemplateErrors(data.roleTemplates);
      if (fieldErrors.some((msg) => !!msg)) {
        // Prevent submit if there are templateId uniqueness errors
        return;
      }

      // Filter out empty templates
      const valuesToSend: IADGroupRequest = {
        ...data,
        roleTemplates: data.roleTemplates.filter(
          (role) => role.templateId && role.templateId > 0,
        ),
      };

      setIsLoading(true);
      await updateADGroup(
        organizationId,
        valuesToSend,
        authenticatedRequest,
        !!adGroupData,
      );
      onClose();
      notify({
        message: adGroupData
          ? t("Organizations:ad_group_edited")
          : t("Organizations:ad_group_created"),
      });
      updateTableFunc(initUpdateTable + 1);
    } catch (err: any) {
      const errorText = (err[0] as ApiV2Error).errorMessage;
      notify({
        variant: "error",
        message: t(`Errors:${errorText}`),
      });
    } finally {
      setIsLoading(false);
    }
  };

  const recomputeErrors = () => {
    const currentTemplates = getValues("roleTemplates");
    recomputeTemplateErrors(currentTemplates);
  };

  const handleAddRole = async () => {
    // do not add new role if there are errors in the last role
    await trigger([`roleTemplates.${fields.length - 1}`]);
    if (errors.roleTemplates) {
      return;
    }
    append({
      templateId: null,
      responseCenterId: null,
      organizationId: null,
    });
    recomputeErrors();
  };

  const handleRemoveRole = (index: number) => {
    remove(index);
    recomputeErrors();
  };

  return (
    <PrimeModal
      withFooter
      withHeader
      onClose={onClose}
      isOpen={isOpen}
      loading={isLoading}
      header={`${
        adGroupData
          ? t("Organizations:ad_edit_mappings")
          : t("Organizations:ad_create_mappings")
      }`}
      fixedWidth="880px"
      submitBtn={{
        text: `${
          adGroupData
            ? t("Organizations:ad_edit_mappings_button")
            : t("Organizations:ad_create_mappings_button")
        }`,
        onClick: handleSubmit(onSubmit),
        disabled: !isDirty || isLoading,
      }}
      cancelBtn={{ text: t("Common:cancel"), onClick: onClose }}
    >
      <p className="grey-600">{t("Organizations:ad_group_description")}</p>

      <Spacer size={32} />
      <FormProvider {...formMethods}>
        <Row type="left" align="start">
          <div style={{ width: "356px" }}>
            <Controller
              name="adGroupName"
              control={control}
              rules={{
                required: true,
              }}
              render={({ field }) => (
                <TextInput
                  required
                  inputRef={field.ref}
                  css={{ flex: 1 }}
                  label={t("Common:name")}
                  placeholder={t("Organizations:ad_group_name_placeholder")}
                  value={field.value}
                  onChange={field.onChange}
                  validationError={
                    errors.adGroupName && t("Errors:name_required")
                  }
                />
              )}
            />
          </div>

          <Spacer size={32} />

          <div style={{ width: "356px" }}>
            <Controller
              name="adGroupId"
              control={control}
              rules={{
                required: true,
              }}
              render={({ field }) => (
                <TextInput
                  required
                  inputRef={field.ref}
                  css={{ flex: 1 }}
                  label={t("Organizations:ad_group_id")}
                  placeholder={t("Organizations:ad_group_id_placeholder")}
                  value={field.value}
                  onChange={field.onChange}
                  validationError={errors.adGroupId && t("Errors:id_required")}
                  disabled={!!adGroupData}
                />
              )}
            />
          </div>
        </Row>

        <Spacer size={16} />

        {fields.map((field, index) => (
          <Fragment key={field.id}>
            <RoleTemplateRow
              index={index}
              organizationsData={organizationsData}
              generalTemplatesData={roleTemplatesData}
              templateError={templateErrors[index]}
              onTemplateChange={() =>
                recomputeTemplateErrors(getValues("roleTemplates"))
              }
              onRemove={() => handleRemoveRole(index)}
              arcDataCache={arcDataCache}
              loadedArcTemplates={loadedArcTemplates}
            />
            <Spacer size={8} />
          </Fragment>
        ))}

        <Spacer size={8} />

        <Button
          image="plus"
          variant="secondary"
          text={t("Organizations:add_role")}
          onClick={handleAddRole}
        />
      </FormProvider>
    </PrimeModal>
  );
};
